/** Cribbage scoring: hands (fifteens, pairs, runs, flush, nobs) and pegging (fifteen, thirty-one, pairs, runs). */
import { parseCard, type CardId } from '../../core/cards.js';

/** Counting value: ace 1, face cards and tens 10. */
export function cribbageValue(card: CardId): number {
  const r = parseCard(card).rank;
  return r === 14 ? 1 : Math.min(r, 10);
}

/** Run order: ace low (1) … king (13). */
export function cribbageRankOf(card: CardId): number {
  const r = parseCard(card).rank;
  return r === 14 ? 1 : r;
}

export interface CribbageScoreItem {
  type: 'fifteen' | 'pair' | 'run' | 'flush' | 'nobs' | 'heels';
  points: number;
  cards: CardId[];
}

export interface CribbageHandScore {
  points: number;
  items: CribbageScoreItem[];
}

/**
 * Scores a four-card hand (or crib) with the starter. With `starter` null it scores the four cards alone, which is
 * what a player evaluates while still discarding.
 */
export function cribbageScoreHand(hand: readonly CardId[], starter: CardId | null, isCrib: boolean): CribbageHandScore {
  const cards = starter ? [...hand, starter] : [...hand];
  const items: CribbageScoreItem[] = [];

  // Fifteens: every subset that adds up to 15.
  const n = cards.length;
  for (let mask = 1; mask < 1 << n; mask++) {
    let sum = 0;
    const sub: CardId[] = [];
    for (let i = 0; i < n; i++) {
      if (mask & (1 << i)) {
        sum += cribbageValue(cards[i]!);
        sub.push(cards[i]!);
      }
    }
    if (sum === 15) items.push({ type: 'fifteen', points: 2, cards: sub });
  }

  // Pairs.
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      if (cribbageRankOf(cards[i]!) === cribbageRankOf(cards[j]!)) items.push({ type: 'pair', points: 2, cards: [cards[i]!, cards[j]!] });
    }
  }

  // Runs: every maximal block of consecutive ranks (length 3+) scores length × the number of ways to pick its cards.
  const byRank = new Map<number, CardId[]>();
  for (const c of cards) byRank.set(cribbageRankOf(c), [...(byRank.get(cribbageRankOf(c)) ?? []), c]);
  let r = 1;
  while (r <= 13) {
    if (!byRank.has(r)) {
      r++;
      continue;
    }
    let end = r;
    while (byRank.has(end + 1)) end++;
    const len = end - r + 1;
    if (len >= 3) {
      let ways = 1;
      const used: CardId[] = [];
      for (let k = r; k <= end; k++) {
        ways *= byRank.get(k)!.length;
        used.push(...byRank.get(k)!);
      }
      items.push({ type: 'run', points: len * ways, cards: used });
    }
    r = end + 1;
  }

  // Flush: four in hand (five with the starter); a crib needs all five.
  if (hand.length === 4) {
    const suit = parseCard(hand[0]!).suit;
    if (hand.every((c) => parseCard(c).suit === suit)) {
      const withStarter = starter !== null && parseCard(starter).suit === suit;
      if (withStarter) items.push({ type: 'flush', points: 5, cards: [...hand, starter!] });
      else if (!isCrib) items.push({ type: 'flush', points: 4, cards: [...hand] });
    }
  }

  // Nobs: the jack of the starter's suit in hand.
  if (starter) {
    const suit = parseCard(starter).suit;
    const jack = hand.find((c) => parseCard(c).rank === 11 && parseCard(c).suit === suit);
    if (jack) items.push({ type: 'nobs', points: 1, cards: [jack] });
  }

  return { points: items.reduce((p, i) => p + i.points, 0), items };
}

export interface CribbagePegScore {
  points: number;
  reasons: string[];
}

/** Points for the card just added to `pile` (the pile already includes it); `count` is the running total. */
export function cribbagePegScore(pile: readonly CardId[], count: number): CribbagePegScore {
  let points = 0;
  const reasons: string[] = [];
  if (count === 15) {
    points += 2;
    reasons.push('fifteen');
  }
  if (count === 31) {
    points += 2;
    reasons.push('thirtyone');
  }
  // Pairs: how many identical ranks end the pile.
  let same = 1;
  for (let i = pile.length - 2; i >= 0 && cribbageRankOf(pile[i]!) === cribbageRankOf(pile[pile.length - 1]!); i--) same++;
  if (same >= 2) {
    points += same === 2 ? 2 : same === 3 ? 6 : 12;
    reasons.push(same === 2 ? 'pair' : same === 3 ? 'pairRoyal' : 'doubleRoyal');
  }
  // Runs: the longest tail of 3+ cards whose ranks are consecutive (in any order).
  for (let len = Math.min(pile.length, 7); len >= 3; len--) {
    const ranks = pile.slice(-len).map(cribbageRankOf);
    const set = new Set(ranks);
    if (set.size === len && Math.max(...ranks) - Math.min(...ranks) === len - 1) {
      points += len;
      reasons.push(`run${len}`);
      break;
    }
  }
  return { points, reasons };
}
